import { type ReactElement } from 'react';
import { type DehydratedState } from '@tanstack/react-query';
import { type PageTrackingTypes } from '@pkgs/components/types/tracking';
import { type Language, type MarketCountryCode } from '@generated/search-bff';
import { Wrapper } from '@ev-tech/ui-core-components/Wrapper';

import { type PageVariant } from '@/types/expose';
import { ListingPageProviders } from '@/providers/ListingPageProviders';
import { type AppPageProps } from '@/pages/_app';
import { PropertyInfoContainer } from '@/components/property-expose/PropertyInfoContainer/PropertyInfoContainer';
import { DynamicPropertyFeatures } from '@/components/property-expose/PropertyFeatures/DynamicPropertyFeatures';
import { KeyInfo } from '@/components/property-expose/KeyInfo/KeyInfo';
import { ContactInfo } from '@/components/property-expose/ContactInfo/ContactInfo';
import { AgentShopCard } from '@/components/property-expose/AgentShopCard/AgentShopCard';
import {
  ActionBar,
  ContactFormSection,
  DynamicCourtagePassus,
  DynamicFinancingSection,
  DynamicFloorPlanSection,
  DynamicLocationText,
  DynamicMediaSection,
  DynamicParentExposeLink,
  DynamicPropertyDescription,
  DynamicPropertyDetails,
  DynamicSimilarProperties,
  DynamicSimilarPropertiesButton,
  ExposeHeadline,
  ExposeHeroImage,
  ExposeLayout,
  LocationBreadcrumb,
  PropertyPriceLabel,
  StickyFooter,
  StickyHeader,
  WavyPattern,
} from '@/components/property-expose';
import { ExposeHead } from '@/components/common/ExposeHead/ExposeHead';

import styles from '@/styles/listingPage.module.css';

export type PropertyExposePageProps = AppPageProps & {
  listingId: string;
  hasLicencePartnerGAAccount: boolean;
  countryCode: MarketCountryCode;
  pageVariant: PageVariant;
  pathLanguage: Language;
  dehydratedState: DehydratedState;
  promotedBy?: string;
  privateOfficePassword?: string;
  pageTrackingTypes: PageTrackingTypes;
};

const ExposePage = (pageProps: PropertyExposePageProps): ReactElement => {
  return (
    <ListingPageProviders {...pageProps}>
      <div className={styles.pageContainer} data-testid="expose-page-container">
        <ExposeHead />
        <ExposeLayout>
          <StickyFooter />
          <ActionBar />
          <StickyHeader />
          <ExposeHeroImage />
          <Wrapper className={styles.exposeContentWrapper} width={{ laptop: 'l', tabletLandscape: 'l' }}>
            <div className={styles.contactCardColumnContainer}>
              <PropertyInfoContainer>
                <LocationBreadcrumb />
                <ExposeHeadline />
                <PropertyPriceLabel />
                <DynamicParentExposeLink />
                <KeyInfo />
                <DynamicPropertyDescription />
                <DynamicPropertyFeatures />
              </PropertyInfoContainer>
              <AgentShopCard />
            </div>
          </Wrapper>
          <DynamicPropertyDetails />
          <DynamicLocationText />
          <DynamicFloorPlanSection />
          <DynamicMediaSection />
          <ContactFormSection />
          <DynamicFinancingSection />
          <DynamicSimilarProperties />
          <DynamicSimilarPropertiesButton />
          <ContactInfo />
          <DynamicCourtagePassus />
          <WavyPattern />
        </ExposeLayout>
      </div>
    </ListingPageProviders>
  );
};

export default ExposePage;
export { exposePageGetServerSideProps as getServerSideProps } from '@/services/SSRFunctions/PropertyExpose/getServerSideProps';
